/**
 * MADEKTA — ui.js
 * Componentes de interfaz globales: paneles laterales, encabezado sticky,
 * acciones de apertura y botón de WhatsApp dinámico.
 */
import { trapFocus, getFocusable, buildWhatsAppUrl } from '@madekta/core';

let openDrawers = 0;

/**
 * <m-drawer id="..."> — panel accesible (role=dialog) con trampa de foco,
 * cierre con Escape / clic en el fondo y restauración del foco.
 */
class MDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.drawer__panel');
    if (!this.isOpen) this.setAttribute('inert', '');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /** @param {HTMLElement | null} [opener] */
  open(opener = /** @type {HTMLElement} */ (document.activeElement)) {
    if (this.isOpen) return;
    this.opener = opener;
    this.setAttribute('open', '');
    this.removeAttribute('inert');
    openDrawers += 1;
    document.body.classList.add('is-locked');
    this.releaseFocus = trapFocus(this.panel);
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => {
      const target = this.querySelector('[data-autofocus]') ?? getFocusable(this.panel)[0];
      target?.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('drawer:open'));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('inert', '');
    openDrawers = Math.max(0, openDrawers - 1);
    if (!openDrawers) document.body.classList.remove('is-locked');
    this.releaseFocus?.();
    document
      .querySelectorAll(`[aria-controls="${this.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.opener?.focus?.({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('drawer:close'));
  }
}
customElements.define('m-drawer', MDrawer);

/* Cualquier botón con data-drawer-open="ID" abre ese panel. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (drawer instanceof MDrawer) {
    event.preventDefault();
    drawer.open(trigger);
  }
});

/**
 * <sticky-header> — oculta el encabezado al bajar y lo muestra al subir;
 * en modo superpuesto cambia a fondo sólido tras el hero.
 */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header');
    this.lastY = window.scrollY;
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  update() {
    const y = window.scrollY;
    const threshold = this.header.offsetHeight;
    this.header.classList.toggle('is-scrolled', y > 8);
    const hidden = y > threshold * 3 && y > this.lastY && !document.body.classList.contains('is-locked');
    this.header.classList.toggle('is-hidden', hidden && !this.header.contains(document.activeElement));
    this.lastY = y;
    this.ticking = false;
  }
}
customElements.define('sticky-header', StickyHeader);

/**
 * <whatsapp-link data-number data-template data-vars='{"producto": "..."}'>
 * Construye el enlace en el cliente para incluir la URL actual.
 */
class WhatsAppLink extends HTMLElement {
  connectedCallback() {
    const anchor = this.querySelector('a');
    if (!anchor) return;
    let vars = {};
    try {
      vars = JSON.parse(this.dataset.vars || '{}');
    } catch {
      vars = {};
    }
    const url = buildWhatsAppUrl(this.dataset.number, this.dataset.template, {
      url: window.location.origin + window.location.pathname,
      ...vars,
    });
    if (url) anchor.href = url;
  }
}
customElements.define('whatsapp-link', WhatsAppLink);

/* Botones con data-open-dialog="ID" abren un <dialog> nativo (modal accesible). */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-dialog]');
  if (!trigger) return;
  const dialog = document.getElementById(trigger.dataset.openDialog);
  if (dialog instanceof HTMLDialogElement) {
    event.preventDefault();
    dialog.showModal();
    document.body.classList.add('is-locked');
    dialog.addEventListener('close', () => document.body.classList.remove('is-locked'), { once: true });
  }
});

/* Clic en el fondo del <dialog> lo cierra. */
document.addEventListener('click', (event) => {
  if (event.target instanceof HTMLDialogElement && event.target.open) {
    const rect = event.target.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) event.target.close();
  }
});
